import { Type } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { match, P } from 'ts-pattern';
import { SupportedDex } from '../supported-dex.js';
import { BaseTxSchema } from './base-tx.js';
export const RaydiumV4AccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.RaydiumV4),
    ammAuthority: Type.String(),
    ammOpenOrders: Type.Optional(Type.String()),
    ammTargetOrders: Type.Optional(Type.String()),
    serumProgram: Type.Optional(Type.String()),
    serumMarket: Type.Optional(Type.String()),
    serumBids: Type.Optional(Type.String()),
    serumAsks: Type.Optional(Type.String()),
    serumEventQueue: Type.Optional(Type.String()),
    serumCoinVaultAccount: Type.Optional(Type.String()),
    serumPcVaultAccount: Type.Optional(Type.String()),
    serumVaultSigner: Type.Optional(Type.String()),
    poolPcTokenAccount: Type.String(),
    poolCoinTokenAccount: Type.String(),
    poolCoinMint: Type.String(),
    poolPcMint: Type.String(),
});
export const RaydiumCpmmAccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.RaydiumCPMM),
    inputVault: Type.String(),
    outputVault: Type.String(),
    inputMint: Type.String(),
    outputMint: Type.String(),
    inputTokenProgram: Type.String(),
    outputTokenProgram: Type.String(),
    observationState: Type.String(),
    ammConfig: Type.String(),
    authority: Type.String(),
});
export const RaydiumClmmAccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.RaydiumCLMM),
    inputVault: Type.String(),
    outputVault: Type.String(),
    inputMint: Type.String(),
    outputMint: Type.String(),
    observationState: Type.String(),
});
export const RaydiumLaunchpadAccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.RaydiumLaunchpad),
    authority: Type.String(),
    configId: Type.String(),
    platformId: Type.String(),
    vaultA: Type.String(),
    vaultB: Type.String(),
    mintA: Type.String(),
    mintB: Type.String(),
    tokenProgramA: Type.String(),
    tokenProgramB: Type.String(),
    platformClaimFeeVault: Type.String(),
    creatorClaimFeeVault: Type.String(),
    creator: Type.String(),
});
/**
 * The accounts a pump AMM swap is built from, named the way the program names
 * them.
 *
 * This asked for `coinCreatorAccount`, which exists nowhere: the indexer sends
 * `coinCreatorVaultAccount` and `coinCreatorVaultAuthority`, and those are the
 * two the router puts in the instruction. So every pumpAmm pool failed
 * `Value.Check` and chart-api dropped it — 7,393 pools on 2026-09-21, the
 * second largest dex in the registry, and on 2026-09-22 it was still logging
 * "registry accounts do not match the terminal schema" for batches of 345 at
 * a time. A trade page for one of those pools never left its skeleton.
 */
export const PumpSwapAccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.PumpSwap),
    poolBaseAccount: Type.String(),
    poolQuoteAccount: Type.String(),
    baseMint: Type.String(),
    quoteMint: Type.String(),
    baseTokenProgram: Type.String(),
    quoteTokenProgram: Type.String(),
    coinCreatorVaultAccount: Type.String(),
    coinCreatorVaultAuthority: Type.String(),
    coinCreator: Type.String(),
    protocolFeeRecipient: Type.String(),
    protocolFeeRecipientAccount: Type.String(),
});
/**
 * The accounts a pump.fun bonding-curve swap is built from.
 *
 * pump.fun moved to a base/quote curve — a launch can be quoted in something
 * other than SOL — and added fee sharing, a buyback recipient and volume
 * accumulators. Three of the seven fields this asked for went with that
 * change: `associatedBondingCurve` became `associatedBaseBondingCurve` beside
 * an optional quote one, `tokenProgram` split into base and quote, and
 * `globalVolumeAccumulator` became optional. So every pump.fun pool failed
 * `Value.Check` and chart-api dropped it, in batches of 358 on 2026-09-22.
 *
 * Optional here means optional on the wire: `crates/indexer-schemas/dex_accounts.rs`
 * declares them `Option<Pubkey>`, because an older curve does not carry them.
 * The router errors on each one it needs and does not have
 * (`apps/pathfinderd/src/application/dex/pump_fun.rs`), which is the right
 * place for that to fail — a pool with no price is worse than a swap that
 * refuses to build.
 */
export const PumpFunBuyAccountsSchema = Type.Object({
    // provider: Type.Literal(SupportedDex.PumpFun),
    global: Type.String(),
    baseMint: Type.String(),
    quoteMint: Type.String(),
    baseTokenProgram: Type.String(),
    quoteTokenProgram: Type.Optional(Type.String()),
    associatedTokenProgram: Type.Optional(Type.String()),
    feeRecipient: Type.String(),
    associatedQuoteFeeRecipient: Type.Optional(Type.String()),
    buybackFeeRecipient: Type.Optional(Type.String()),
    associatedQuoteBuybackFeeRecipient: Type.Optional(Type.String()),
    bondingCurve: Type.String(),
    associatedBaseBondingCurve: Type.String(),
    associatedQuoteBondingCurve: Type.Optional(Type.String()),
    user: Type.String(),
    associatedBaseUser: Type.String(),
    associatedQuoteUser: Type.Optional(Type.String()),
    creatorVault: Type.String(),
    associatedCreatorVault: Type.Optional(Type.String()),
    sharingConfig: Type.Optional(Type.String()),
    globalVolumeAccumulator: Type.Optional(Type.String()),
    userVolumeAccumulator: Type.Optional(Type.String()),
    associatedUserVolumeAccumulator: Type.Optional(Type.String()),
    feeConfig: Type.Optional(Type.String()),
    feeProgram: Type.Optional(Type.String()),
    systemProgram: Type.Optional(Type.String()),
    eventAuthority: Type.Optional(Type.String()),
    program: Type.Optional(Type.String()),
});
/**
 * The same accounts. `PumpFunAccounts` in the indexer is one struct for both
 * sides, and the program takes the same list to buy and to sell.
 *
 * These were two separate literals, and they drifted: the buy side was updated
 * for the base/quote curve and the sell side was not, so chart-api kept
 * dropping every pump.fun pool on the half nobody had looked at. One
 * definition cannot drift from itself.
 */
export const PumpFunSellAccountsSchema = PumpFunBuyAccountsSchema;
export const MeteoraDlmmAccountsSchema = Type.Object({
    lbPair: Type.String(),
    binArrayBitmapExtension: Type.String(),
    reserveX: Type.String(),
    reserveY: Type.String(),
    userTokenIn: Type.String(),
    userTokenOut: Type.String(),
    tokenXMint: Type.String(),
    tokenYMint: Type.String(),
    tokenXProgram: Type.String(),
    tokenYProgram: Type.String(),
    oracle: Type.String(),
    hostFeeIn: Type.String(),
    eventAuthority: Type.String(),
    memoProgram: Type.Optional(Type.String()),
});
export const DexBuyAccountsSchema = Type.Union([
    RaydiumV4AccountsSchema,
    RaydiumCpmmAccountsSchema,
    RaydiumClmmAccountsSchema,
    RaydiumLaunchpadAccountsSchema,
    PumpSwapAccountsSchema,
    PumpFunBuyAccountsSchema,
    MeteoraDlmmAccountsSchema,
]);
export const DexSellAccountsSchema = Type.Union([
    RaydiumV4AccountsSchema,
    RaydiumCpmmAccountsSchema,
    RaydiumClmmAccountsSchema,
    RaydiumLaunchpadAccountsSchema,
    PumpSwapAccountsSchema,
    PumpFunSellAccountsSchema,
    MeteoraDlmmAccountsSchema,
]);
const SwapVariant = (fromDex, operation, accounts) => Type.Object({
    fromDex: Type.Union([Type.Literal(fromDex), Type.Literal(fromDex)]),
    operation: Type.Union([Type.Literal(operation), Type.Literal(operation)]),
    accounts,
});
export const DexAccountsSchema = Type.Union([
    SwapVariant(SupportedDex.RaydiumV4, 'buy', RaydiumV4AccountsSchema),
    SwapVariant(SupportedDex.RaydiumV4, 'sell', RaydiumV4AccountsSchema),
    SwapVariant(SupportedDex.RaydiumCPMM, 'buy', RaydiumCpmmAccountsSchema),
    SwapVariant(SupportedDex.RaydiumCPMM, 'sell', RaydiumCpmmAccountsSchema),
    SwapVariant(SupportedDex.RaydiumCLMM, 'buy', RaydiumClmmAccountsSchema),
    SwapVariant(SupportedDex.RaydiumCLMM, 'sell', RaydiumClmmAccountsSchema),
    SwapVariant(SupportedDex.RaydiumLaunchpad, 'buy', RaydiumLaunchpadAccountsSchema),
    SwapVariant(SupportedDex.RaydiumLaunchpad, 'sell', RaydiumLaunchpadAccountsSchema),
    SwapVariant(SupportedDex.BonkFun, 'buy', RaydiumLaunchpadAccountsSchema),
    SwapVariant(SupportedDex.BonkFun, 'sell', RaydiumLaunchpadAccountsSchema),
    SwapVariant(SupportedDex.PumpFun, 'buy', PumpFunBuyAccountsSchema),
    SwapVariant(SupportedDex.PumpFun, 'sell', PumpFunSellAccountsSchema),
    SwapVariant(SupportedDex.PumpSwap, 'buy', PumpSwapAccountsSchema),
    SwapVariant(SupportedDex.PumpSwap, 'sell', PumpSwapAccountsSchema),
]);
export const SwapDexAccountsSchema = Type.Optional(Type.Object({
    buy: Type.Optional(DexBuyAccountsSchema),
    sell: Type.Optional(DexSellAccountsSchema),
}));
export const SwapTxFeesSchema = Type.Object({
    mint: Type.String(),
    treasury: Type.Number(),
    referrer: Type.Number(),
    total: Type.Number(),
    referralCode: Type.Union([Type.String(), Type.Null()]),
});
export const ProviderFeePaymentSchema = Type.Object({
    mint: Type.String(),
    amount: Type.Union([Type.String(), Type.Number()]),
});
export const KNOWN_FEE_PROVIDERS = [
    'axiom-terminal',
    'gmgn-terminal',
    'padre-terminal',
    'fomo',
    'trojan',
];
export const ProviderFeeSchema = Type.Object({
    provider: Type.Unsafe(Type.String()),
    payments: Type.Array(ProviderFeePaymentSchema),
});
export const SwapDexAccountsRawSchema = Type.Object({
    buy: Type.Optional(Type.Record(Type.String(), Type.String())),
    sell: Type.Optional(Type.Record(Type.String(), Type.String())),
});
export const SwapFields = {
    operation: Type.Union([Type.Literal('sell'), Type.Literal('buy')]),
    accounts: Type.Optional(Type.Record(Type.String(), Type.Union([Type.String(), Type.Array(Type.String())]))),
    fees: Type.Optional(SwapTxFeesSchema),
    providerFee: Type.Optional(Type.Union([ProviderFeeSchema, Type.Null()])),
    balanceBefore: Type.String(),
    balanceAfter: Type.String(),
    balanceChanged: Type.String(),
    balanceChangedPercent: Type.String(),
};
export const SwapTxSchema = Type.Object({
    ...BaseTxSchema.properties,
    ...SwapFields,
});
export const isValidSwapDexAccount = (operation, dex, accounts) => {
    const schema = match([dex, operation])
        .with([SupportedDex.RaydiumV4, P.any], () => RaydiumV4AccountsSchema)
        .with([SupportedDex.RaydiumCPMM, P.any], () => RaydiumCpmmAccountsSchema)
        .with([SupportedDex.RaydiumCLMM, P.any], () => RaydiumClmmAccountsSchema)
        .with([SupportedDex.RaydiumLaunchpad, P.any], () => RaydiumLaunchpadAccountsSchema)
        .with([SupportedDex.BonkFun, P.any], () => RaydiumLaunchpadAccountsSchema)
        .with([SupportedDex.PumpFun, 'buy'], () => PumpFunBuyAccountsSchema)
        .with([SupportedDex.PumpFun, 'sell'], () => PumpFunSellAccountsSchema)
        .with([SupportedDex.PumpSwap, P.any], () => PumpSwapAccountsSchema)
        .otherwise(() => Type.Any());
    if (!accounts) {
        return true;
    }
    return Value.Check(schema, accounts);
};
//# sourceMappingURL=swap-tx.js.map